import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { getDevToken, clearDevToken } from '../auth/devSession';

/**
 * Provider-neutral account shape, replacing MSAL's AccountInfo.
 *
 * Callers only ever read `idTokenClaims` (for extension_Role and the name claims) plus
 * `username`, so this exposes exactly that rather than leaking an oidc-client-ts type
 * across the app the way `AccountInfo` leaked MSAL.
 */
export interface AppAccount {
  /**
   * The identity provider's stable user id - the OIDC `sub` claim, which under Keycloak
   * is the user's UUID. Replaces MSAL's `localAccountId` (the Entra object id).
   *
   * NOTE: this is the IdP's id, not TransitDeal's own UserId from the database. Code
   * comparing it against a TransitDeal UserId was already wrong under MSAL and still is.
   */
  userId: string;
  username: string;
  name?: string;
  idTokenClaims?: Record<string, unknown>;
}

export interface AppAuth {
  account: AppAccount | undefined;
  isAuthenticated: boolean;
  getToken: () => Promise<string>;
  login: () => void;
  logout: () => void;
  changePassword: () => void;
}

export function useAppAuth(): AppAuth {
  const auth = useAuth();
  const navigate = useNavigate();

  const devToken = import.meta.env.DEV ? getDevToken() : null;
  const isDev = import.meta.env.DEV && !!devToken;

  // MUST be memoised. Callers put `account` in useCallback/useEffect dependency arrays
  // (see JobsBoardPage's `load`), so returning a fresh object literal each render makes
  // those callbacks change identity every render, which re-fires the effect, which sets
  // state, which renders again - an unbounded request loop that ends in
  // ERR_INSUFFICIENT_RESOURCES. MSAL's accounts[0] was a stable cache reference and hid
  // this; oidc-client-ts hands back a profile that has to be reshaped, so the stability
  // has to be reintroduced here.
  //
  // auth.user is itself stable between token changes, so it is the right dependency.
  const account = useMemo<AppAccount | undefined>(() => {
    // All dev tokens are user GUIDs — display name and role come from /api/v1/users/me.
    if (isDev) {
      return { userId: devToken!, username: devToken!, name: undefined, idTokenClaims: {} };
    }
    if (!auth.user) return undefined;
    return {
      userId: auth.user.profile.sub,
      username: (auth.user.profile.preferred_username as string) ?? auth.user.profile.sub,
      name: auth.user.profile.name,
      idTokenClaims: auth.user.profile as unknown as Record<string, unknown>,
    };
  }, [isDev, devToken, auth.user]);

  const getToken = useCallback(async (): Promise<string> => {
    if (isDev) return devToken!;

    // oidc-client-ts renews in the background (automaticSilentRenew), so the stored token
    // is normally current. If it has expired anyway - laptop asleep through the renewal
    // window, say - force one renewal rather than handing back a token the API will 401.
    if (auth.user && !auth.user.expired) return auth.user.access_token;

    const renewed = await auth.signinSilent();
    if (!renewed?.access_token) throw new Error('Unable to obtain an access token');
    return renewed.access_token;
  }, [isDev, devToken, auth.user, auth.signinSilent]);

  const login = useCallback(() => {
    if (import.meta.env.DEV) {
      navigate('/dev-login');
    } else {
      void auth.signinRedirect();
    }
  }, [navigate, auth.signinRedirect]);

  const logout = useCallback(() => {
    if (import.meta.env.DEV && devToken) {
      clearDevToken();
      window.location.href = '/';
      return;
    }
    // signoutRedirect hits Keycloak's end_session endpoint, which clears the SSO cookie
    // as well as the local token. Removing only the local user would leave the browser
    // session live, so the next "Sign in" would silently log straight back in.
    void auth.signoutRedirect();
  }, [devToken, auth.signoutRedirect]);

  /**
   * Keycloak has a built-in account console for password changes, so there is no
   * equivalent of the Entra "change password" policy authority to redirect to.
   */
  const changePassword = useCallback(() => {
    if (import.meta.env.DEV) {
      alert('Password change is handled by your identity provider.');
      return;
    }
    const authority = import.meta.env.VITE_OIDC_AUTHORITY as string;
    window.location.href = `${authority}/account/#/security/signingin`;
  }, []);

  // Memoised for the same reason as `account`: a new object each render defeats any
  // consumer that depends on the hook's result as a whole.
  return useMemo(
    () => ({
      account,
      isAuthenticated: isDev ? true : auth.isAuthenticated,
      getToken,
      login,
      logout,
      changePassword,
    }),
    [account, isDev, auth.isAuthenticated, getToken, login, logout, changePassword],
  );
}
